import { useEffect, useState } from 'react'

/**
 * Where a dropdown should render when the page might be in full screen.
 *
 * Mantine portals its menus and comboboxes to document.body, which is correct
 * everywhere except inside a fullscreen element: the browser only paints the
 * fullscreen subtree, so a dropdown portalled to the body is mounted, focusable
 * and completely invisible. That is what made the stint chart's car filter look
 * broken on a pit wall while working perfectly on a desk.
 *
 * The naive fix — turning the portal off — trades one bug for another, because
 * these controls sit inside scrolling containers that would then clip the
 * dropdown. So the portal stays and only its destination moves.
 *
 * Returns undefined when not in full screen, which is Mantine's own default.
 */
export function usePortalTarget(): HTMLElement | undefined {
  const [target, setTarget] = useState<HTMLElement | undefined>(undefined)

  useEffect(() => {
    const read = () => setTarget((document.fullscreenElement as HTMLElement | null) ?? undefined)
    read()
    document.addEventListener('fullscreenchange', read)
    return () => document.removeEventListener('fullscreenchange', read)
  }, [])

  return target
}
